import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import DetailsLoader, {detailsLoaderQuery} from '../../loaders/DetailsLoader';
import {LoadMore} from '../../../../../loaders/LoadMore';
import {BudgetEyeOptions} from '../../BudgetPage.EyeOptions';
import {BUDGET_TYPE, BUDGET_VIEWS, PERIOD_BUDGET_TYPE, SPECIAL_ROLE} from '../../../../../../constants';
import {BudgetTooltips} from '../../util/BudgetTooltips';
import ExpenseDetailsTable from './ExpenseDetailsTable';
import {filterAggregateFields} from '../../util/BudgetUtils';
import {useRemappingFormatMessage} from '../../../../../../forecast-app/shared/util/FinancialInternationalisationUtil';
import {hasFeatureFlag} from '../../../../../../forecast-app/shared/util/FeatureUtil';

const ExpenseDetails = ({project, selectedAggregateLevel, selectedViewType, eyeOptionMap}) => {
	const formatMessage = useRemappingFormatMessage();
	const {useBaseline} = project;

	const searchQuery = {
		filters: [
			{
				field: 'ROLE',
				operator: 'IN',
				value: SPECIAL_ROLE.EXPENSE,
			},
		],
	};

	const isFixedPrice =
		project.budgetType === BUDGET_TYPE.FIXED_PRICE ||
		project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 ||
		(project.budgetType === BUDGET_TYPE.RETAINER && project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE);

	const baselineAggregates = filterAggregateFields(project, ['baselineRevenue', 'baselineCost', 'baselineProfit']);
	const baselineVsActualAggregates = filterAggregateFields(project, [
		'baselineVsActualRevenue',
		'baselineVsActualCost',
		'baselineVsActualProfit',
	]);
	const baselineVsForecastAggregates = filterAggregateFields(project, [
		'baselineVsForecastRevenue',
		'baselineVsForecastCost',
		'baselineVsForecastProfit',
	]);
	const actualAggregates = filterAggregateFields(project, ['actualRevenue', 'actualCost', 'actualProfit']);
	const planAggregates = filterAggregateFields(project, ['plannedRevenue', 'plannedCost', 'plannedProfit']);
	const actualVsPlanAggregates = filterAggregateFields(project, [
		'actualVsPlanRevenue',
		'actualVsPlanCost',
		'actualVsPlanProfit',
	]);
	const remainingAggregates = filterAggregateFields(project, ['remainingRevenue', 'remainingCost', 'remainingProfit']);
	const forecastAggregates = filterAggregateFields(project, ['forecastRevenue', 'forecastCost', 'forecastProfit']);
	const forecastVsPlanAggregates = filterAggregateFields(project, [
		'forecastVsPlanRevenue',
		'forecastVsPlanCost',
		'forecastVsPlanProfit',
	]);

	const aggregates = [
		...baselineAggregates,
		...baselineVsActualAggregates,
		...baselineVsForecastAggregates,
		...actualAggregates,
		...planAggregates,
		...actualVsPlanAggregates,
		...remainingAggregates,
		...forecastAggregates,
		...forecastVsPlanAggregates,
	];

	return (
		<>
			<LoadMore
				key="query-render-details"
				query={detailsLoaderQuery}
				variables={{
					projectId: project.id,
					startYear: project.projectFirstDateYear,
					startMonth: project.projectFirstDateMonth,
					startDay: project.projectFirstDateDay,
					endYear: project.projectLastDateYear,
					endMonth: project.projectLastDateMonth,
					endDay: project.projectLastDateDay,
					searchQuery: searchQuery,
					aggregateLevel: selectedAggregateLevel,
					aggregates: aggregates,
				}}
				componentName="details_loader"
				loader={<DetailsLoader aggregateLevel={selectedAggregateLevel} aggregates={aggregates} />}
			>
				{({data}) => {
					return (
						<>
							{useBaseline && eyeOptionMap[BudgetEyeOptions.BASELINE] && (
								<ExpenseDetailsTable
									key={`baseline-${selectedAggregateLevel}`}
									title={formatMessage({id: 'baseline.baseline'})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									data={data}
									aggregates={baselineAggregates}
									selectedViewType={selectedViewType}
									searchQuery={searchQuery}
									tooltips={BudgetTooltips.BaselineTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType
									)}
								/>
							)}
							{useBaseline && eyeOptionMap[BudgetEyeOptions.BASELINE_VS_ACTUAL] && (
								<ExpenseDetailsTable
									key={`baseline-vs-actual-${selectedAggregateLevel}`}
									title={formatMessage({id: 'project_budget.baseline_vs_actual'})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									data={data}
									aggregates={baselineVsActualAggregates}
									selectedViewType={selectedViewType}
									searchQuery={searchQuery}
								/>
							)}
							{useBaseline && eyeOptionMap[BudgetEyeOptions.BASELINE_VS_FORECAST] && (
								<ExpenseDetailsTable
									key={`baseline-vs-forecast-${selectedAggregateLevel}`}
									title={formatMessage({id: 'project_budget.baseline_vs_forecast'})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									data={data}
									aggregates={baselineVsForecastAggregates}
									selectedViewType={selectedViewType}
									searchQuery={searchQuery}
								/>
							)}
							{eyeOptionMap[BudgetEyeOptions.ACTUAL] ? (
								<ExpenseDetailsTable
									key={`actual-${selectedAggregateLevel}`}
									title={formatMessage({id: 'project_budget.actual'})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={actualAggregates}
									selectedViewType={selectedViewType}
									data={data}
									searchQuery={searchQuery}
									tooltips={BudgetTooltips.ActualTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										isFixedPrice
									)}
								/>
							) : (
								''
							)}
							{eyeOptionMap[BudgetEyeOptions.PLAN] ? (
								<ExpenseDetailsTable
									key={`plan-${selectedAggregateLevel}`}
									title={formatMessage({id: 'common.planned'})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={planAggregates}
									selectedViewType={selectedViewType}
									data={data}
									searchQuery={searchQuery}
									tooltips={BudgetTooltips.PlanTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										isFixedPrice
									)}
								/>
							) : (
								''
							)}
							{eyeOptionMap[BudgetEyeOptions.ACTUAL_VS_PLAN] ? (
								<ExpenseDetailsTable
									key={`actualVsPlan-${selectedAggregateLevel}`}
									title={formatMessage({id: 'project_budget.actual_vs_plan'})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={actualVsPlanAggregates}
									selectedViewType={selectedViewType}
									data={data}
									searchQuery={searchQuery}
									tooltips={BudgetTooltips.ActualVsPlanTooltips(formatMessage, BUDGET_VIEWS.MONEY)}
								/>
							) : (
								''
							)}
							{eyeOptionMap[BudgetEyeOptions.REMAINING] ? (
								<ExpenseDetailsTable
									key={`remaining-${selectedAggregateLevel}`}
									title={formatMessage({
										id: hasFeatureFlag('revenue_recognition')
											? 'project_budget.forecast_to_complete'
											: 'common.remaining',
									})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={remainingAggregates}
									selectedViewType={selectedViewType}
									data={data}
									searchQuery={searchQuery}
									tooltips={BudgetTooltips.RemainingTooltips(
										formatMessage,
										BUDGET_VIEWS.MONEY,
										selectedViewType,
										isFixedPrice
									)}
								/>
							) : (
								''
							)}
							{eyeOptionMap[BudgetEyeOptions.FORECAST] ? (
								<ExpenseDetailsTable
									key={`forecast-${selectedAggregateLevel}`}
									title={formatMessage({
										id: hasFeatureFlag('revenue_recognition')
											? 'project_budget.total_at_completion'
											: 'common.forecast',
									})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={forecastAggregates}
									selectedViewType={selectedViewType}
									data={data}
									searchQuery={searchQuery}
									tooltips={BudgetTooltips.ForecastTooltips(formatMessage, BUDGET_VIEWS.MONEY)}
								/>
							) : (
								''
							)}
							{eyeOptionMap[BudgetEyeOptions.FORECAST_VS_PLAN] ? (
								<ExpenseDetailsTable
									key={`forecastVsPlan-${selectedAggregateLevel}`}
									title={formatMessage({id: 'project_budget.forecast_vs_plan'})}
									project={project}
									selectedAggregateLevel={selectedAggregateLevel}
									aggregates={forecastVsPlanAggregates}
									selectedViewType={selectedViewType}
									data={data}
									searchQuery={searchQuery}
									tooltips={BudgetTooltips.ForecastVsPlanTooltips(formatMessage, BUDGET_VIEWS.MONEY)}
								/>
							) : (
								''
							)}
						</>
					);
				}}
			</LoadMore>
		</>
	);
};

export default createFragmentContainer(ExpenseDetails, {
	project: graphql`
		fragment ExpenseDetails_project on ProjectType {
			id
			useBaseline
			budgetType
			defaultPeriodBudgetType
			projectFirstDateYear
			projectFirstDateMonth
			projectFirstDateDay
			projectLastDateYear
			projectLastDateMonth
			projectLastDateDay
			billable
			...ExpenseDetailsTable_project
		}
	`,
});
